// task-36: custom hook that fetches the canonical payment-provider list from the backend once.
// Both AdminUsersPage and AdminPaymentEventsPage call this hook so the list is fetched per mount
// rather than once globally; React's batching ensures only one in-flight request per page.
// Falls back to an empty array while loading — view components must handle [] gracefully.

import { useState, useEffect } from 'react';
import { fetchSupportedProviders } from './admin.api'; // shared fetch function targeting GET /api/admin/providers

interface UseSupportedProvidersResult {
  providers: string[];  // canonical list from backend; empty while loading or on error
  loading: boolean;     // true during the initial fetch
}

/**
 * Fetches and caches the canonical supported-provider list from GET /api/admin/providers.
 * Errors are non-fatal: the hook logs them and returns an empty list so views degrade gracefully.
 *
 * @returns UseSupportedProvidersResult — { providers, loading }
 */
export function useSupportedProviders(): UseSupportedProvidersResult {
  const [providers, setProviders] = useState<string[]>([]); // empty until resolved
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false; // prevent state update if the component unmounts before fetch completes
    fetchSupportedProviders()
      .then((list) => { if (!cancelled) setProviders(list); }) // apply canonical list from backend
      .catch((err) => { // non-fatal: view continues to render with an empty list
        if (!cancelled) console.error('[useSupportedProviders] fetch failed:', err instanceof Error ? err.message : String(err));
      })
      .finally(() => { if (!cancelled) setLoading(false); }); // always stop loading indicator
    return () => { cancelled = true; }; // cleanup on unmount to avoid memory leaks
  }, []); // run once per mount — provider list is stable across a page session

  return { providers, loading };
}
